import React, { useState, useRef, useEffect } from 'react';
import { toast } from 'sonner';
import { signUp, signIn, resetPassword } from '../../lib/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Building2, Music, Users, Calendar, MessageSquare, Sparkles, Camera, X, Loader2, Cake, AlertCircle, CheckCircle2, Eye, EyeOff } from 'lucide-react';
import { useDesign } from '@/contexts/DesignContext';
import DownloadAppContent from '@/components/shared/DownloadAppContent';
import UploadProgress from '@/components/shared/UploadProgress';
import { uploadWithProgress } from '@/lib/uploadVideoWithProgress';
import { IMAGE_ACCEPT, isImageFile } from '@/lib/imageFiles';
import LocalImageThumb from '@/components/shared/LocalImageThumb';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper function to fetch user info from backend
const fetchUserInfo = async (userId) => {
  try {
    const response = await fetch(`${API_URL}/api/auth/user-info/${userId}`);
    if (response.ok) {
      return await response.json();
    }
  } catch (err) {
    console.error('[AuthPage] Failed to fetch user info:', err);
  }
  return null;
};

const BrandPanel = () => (
  <div className="hidden lg:block lg:w-1/2 relative overflow-hidden" data-testid="auth-brand-panel">
    <img
      src="https://images.unsplash.com/photo-1550633600-4cfc6929e592?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400"
      alt="Worship team in a moment of worship"
      className="absolute inset-0 w-full h-full object-cover"
    />
    <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/30 to-black/40"></div>
    <div className="relative z-10 h-full flex flex-col justify-between p-12 rise-in">
      <div>
        <div className="flex items-center gap-3 mb-10">
          <img
            src="https://customer-assets.emergentagent.com/job_praiseleadsys/artifacts/dqkpm4bx_Untitled%20design%20%284%29.png"
            alt="Worship OS"
            className="w-12 h-12 rounded-xl object-contain"
          />
          <span className="text-white text-xl font-medium font-heading tracking-tight">Worship OS</span>
        </div>
        <h1 className="text-5xl font-medium text-white leading-[1.05] tracking-tight font-heading mb-5">
          Lead your team<br />with confidence
        </h1>
        <p className="text-white/80 text-lg max-w-sm">
          The all-in-one platform for worship ministry leaders
        </p>
      </div>
      <div className="space-y-3">
        {[
          { icon: Calendar, label: 'Smart planning & scheduling' },
          { icon: Users, label: 'Team onboarding & training' },
          { icon: MessageSquare, label: 'Team communication hub' },
        ].map(({ icon: Icon, label }) => (
          <div key={label} className="flex items-center gap-3 text-white/90 bg-white/10 backdrop-blur-md rounded-xl px-4 py-3 border border-white/10 w-fit">
            <Icon className="w-4 h-4 text-white" />
            <span className="text-sm font-medium">{label}</span>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_praiseleadsys/artifacts/dqkpm4bx_Untitled%20design%20%284%29.png';

// Studio (doc 119 §24): the split brand panel gives way to the 64 header with
// the mark and wordmark, and the card sits on the ground. The classic panel is
// still rendered — hidden — so its data-testid survives (R2).
const StudioShell = ({ children, top = false }) => (
  <div className="st-au-shell">
    <div className="st-au-hidden"><BrandPanel /></div>
    <div className="st-au-hdr">
      <div data-ui="brand-mark" aria-label="Worship OS" className="st-au-mark">W</div>
      <span className="st-au-wm">Worship OS</span>
    </div>
    <div className={top ? 'st-au-page st-au-top' : 'st-au-page'}>{children}</div>
  </div>
);

const ClassicShell = ({ children }) => (
  <div className="min-h-screen flex">
    {/* Left Panel - Branding */}
    <BrandPanel />

    {/* Right Panel - Form */}
    <div className="flex-1 flex items-center justify-center p-8 bg-slate-50 dark:bg-[#1e1f22]">
      <div className="w-full max-w-md rise-in">
        <div className="lg:hidden flex items-center gap-3 mb-8 justify-center">
          <img
            src={LOGO_URL}
            alt="Worship OS"
            className="w-12 h-12 rounded-xl object-contain"
          />
          <span className="text-slate-800 dark:text-slate-100 text-xl font-semibold">Worship OS</span>
        </div>
        {children}
      </div>
    </div>
  </div>
);

// One notice shape for every banner this page can show (mock frames 4, 6, 7, 25)
const StudioNotice = ({ kind, children }) => (
  <div className={`st-au-notice st-au-${kind}`} role={kind === 'bad' ? 'alert' : undefined}>
    {kind === 'ok' ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
    <span>{children}</span>
  </div>
);

function AuthPage({ onAuth, inviteToken = null, hideSignupOption = false, recoveryError = '', onRecoveryErrorClear }) {
  const { isStudio } = useDesign();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [churchName, setChurchName] = useState('');
  const [isSignUp, setIsSignUp] = useState(!!inviteToken); // Auto-switch to signup if invite token
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [showDownloadApp, setShowDownloadApp] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  
  // Invite info state
  const [inviteInfo, setInviteInfo] = useState(null);
  const [loadingInvite, setLoadingInvite] = useState(!!inviteToken);
  
  // Profile picture state (for signup only)
  const [profilePicture, setProfilePicture] = useState(null);
  const [profilePicturePreview, setProfilePicturePreview] = useState(null);
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const [avatarUploadProgress, setAvatarUploadProgress] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const fileInputRef = useRef(null);
  
  // Birthday state (for signup only)
  const [birthdayMonth, setBirthdayMonth] = useState('');
  const [birthdayDay, setBirthdayDay] = useState('');

  // If we arrived from a dead recovery link, jump straight to the Forgot
  // Password view so the user can request a fresh link immediately.
  useEffect(() => {
    if (recoveryError) setIsForgotPassword(true);
  }, [recoveryError]);

  // Fetch invite info when token is provided
  useEffect(() => {
    const fetchInviteInfo = async () => {
      if (!inviteToken) return;
      
      try {
        setLoadingInvite(true);
        const response = await fetch(`${API_URL}/api/auth/invite/${inviteToken}/info`);
        if (response.ok) {
          const data = await response.json();
          setInviteInfo(data);
          // Pre-fill form fields
          setName(data.invitee_name || '');
          setEmail(data.invitee_email || '');
          setChurchName(data.church_name || '');
        } else {
          const errData = await response.json();
          setError(errData.detail || 'Invalid or expired invitation');
        }
      } catch (err) {
        console.error('Error fetching invite info:', err);
        setError('Unable to load invitation details');
      } finally {
        setLoadingInvite(false);
      }
    };
    
    fetchInviteInfo();
  }, [inviteToken]);

  // Handle profile picture selection
  const handlePictureSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!isImageFile(file)) {
      setError('Please select an image file');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      setError('Image File Size too big. Must be under 5MB');
      return;
    }
    
    setProfilePicture(file);
    setError('');
    
    // A HEIC preview only draws in Safari; LocalImageThumb shows a label
    // elsewhere (the server stores JPEG on upload).
    const reader = new FileReader();
    reader.onloadend = () => {
      setProfilePicturePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleRemovePicture = () => {
    setProfilePicture(null);
    setProfilePicturePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Get initials for avatar placeholder
  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.split(' ').filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return parts[0]?.[0]?.toUpperCase() || '?';
  };

  const handleAuth = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      if (isSignUp) {
        if (!name.trim()) {
          throw new Error('Please enter your name');
        }
        // For new signups (not invites), require church name
        if (!inviteToken && !churchName.trim()) {
          throw new Error('Please enter your church name');
        }
        if (!birthdayMonth || !birthdayDay) {
          throw new Error('Please enter your birthday');
        }
        
        const { data, error: signUpError } = await signUp(email, password, name);
        
        if (signUpError) {
          if (signUpError.message?.includes('email_address_invalid')) {
            throw new Error('Please use a valid email address (e.g., your@gmail.com)');
          }
          throw signUpError;
        }
        
        if (data?.user) {
          // Create church and set user as owner (or join via invite)
          try {
            const setupResponse = await fetch(`${API_URL}/api/auth/setup-account`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                user_id: data.user.id,
                user_email: data.user.email,
                user_name: name,
                church_name: churchName || null,
                invite_token: inviteToken || null,
                birthday: birthdayMonth && birthdayDay ? `${birthdayMonth.padStart(2, '0')}-${birthdayDay.padStart(2, '0')}` : null
              })
            });
            
            if (!setupResponse.ok) {
              const errData = await setupResponse.json();
              throw new Error(errData.detail || 'Failed to setup account');
            }
            
            const setupData = await setupResponse.json();
            
            // Upload profile picture if selected and track the URL
            let uploadedAvatarUrl = null;
            if (profilePicture && data.user.id) {
              setUploadingPicture(true);
              setAvatarUploadProgress(0);
              try {
                const formData = new FormData();
                formData.append('user_id', data.user.id);
                formData.append('name', name);
                formData.append('file', profilePicture);
                
                // Real XHR byte-accurate progress. Reserve 10% for server-side
                // processing (Supabase Storage upload + profile row write).
                const uploadResult = await uploadWithProgress(
                  `${API_URL}/api/avatar/upload`,
                  formData,
                  setAvatarUploadProgress,
                  10,
                );
                console.log('[AuthPage] Avatar uploaded:', uploadResult);
                uploadedAvatarUrl = uploadResult.avatar_url;
                
                // Sync to Firestore for messaging
                try {
                  const { updateUserProfile } = await import('@/lib/firestore');
                  await updateUserProfile(data.user.id, {
                    name: name,
                    avatar: uploadResult.avatar_url
                  });
                  console.log('[AuthPage] Avatar synced to Firestore');
                } catch (firestoreErr) {
                  console.error('[AuthPage] Firestore sync error:', firestoreErr);
                }
              } catch (uploadErr) {
                console.error('[AuthPage] Avatar upload error:', uploadErr);
                toast.info("Your account was created, but your photo couldn't be uploaded. You can add it in Settings.");
              } finally {
                setUploadingPicture(false);
                setAvatarUploadProgress(0);
              }
            }
            
            // Add church info and avatar to user object
            const enrichedUser = {
              ...data.user,
              name: name,
              avatar: uploadedAvatarUrl,
              church_id: setupData.church_id,
              church_name: setupData.church_name,
              church_slug: setupData.church_slug,
              role: setupData.role,
              user_metadata: {
                ...data.user.user_metadata,
                name: name,
                avatar_url: uploadedAvatarUrl
              }
            };
            
            if (!data?.session) {
              // No session returned - try to sign in automatically
              console.log('[AuthPage] No session returned, attempting auto-signin...');
              try {
                const { data: signInData, error: signInErr } = await signIn(email, password);
                if (signInErr) {
                  // If signin fails, show confirmation message
                  setSuccessMessage('Account created! Please check your email for the confirmation link, then sign in.');
                } else if (signInData?.session) {
                  // Successfully signed in after signup
                  const userInfo = await fetchUserInfo(signInData.user.id);
                  const autoLoginUser = {
                    ...signInData.user,
                    name: userInfo?.name || name,
                    avatar: uploadedAvatarUrl || userInfo?.avatar_url,
                    church_id: userInfo?.church_id || setupData.church_id,
                    church_name: userInfo?.church_name || setupData.church_name,
                    role: userInfo?.role || setupData.role,
                    user_metadata: {
                      ...signInData.user.user_metadata,
                      name: userInfo?.name || name,
                      avatar_url: uploadedAvatarUrl || userInfo?.avatar_url
                    }
                  };
                  // Team members → show download app page instead of web app
                  if ((userInfo?.role || setupData.role) === 'team_member') {
                    setShowDownloadApp(true);
                  } else {
                    onAuth(autoLoginUser);
                  }
                }
              } catch (autoSigninErr) {
                console.error('[AuthPage] Auto-signin failed:', autoSigninErr);
                setSuccessMessage('Account created! Please sign in with your new credentials.');
              }
            } else {
              // Team members → show download app page instead of web app
              if (setupData.role === 'team_member') {
                setShowDownloadApp(true);
              } else {
                onAuth(enrichedUser);
              }
            }
          } catch (setupErr) {
            console.error('Account setup error:', setupErr);
            throw new Error(setupErr.message || 'Failed to complete account setup');
          }
        }
      } else {
        const { data, error: signInError } = await signIn(email, password);
        
        if (signInError) {
          if (signInError.message?.includes('Invalid login credentials')) {
            throw new Error('Invalid email or password. Please try again.');
          }
          if (signInError.message?.includes('Email not confirmed')) {
            throw new Error('Please confirm your email address first. Check your inbox!');
          }
          throw signInError;
        }
        
        if (data?.user) {
          // Fetch user's church and role info
          try {
            const userInfoResponse = await fetch(`${API_URL}/api/auth/user-info/${data.user.id}`);
            if (userInfoResponse.ok) {
              const userInfo = await userInfoResponse.json();
              onAuth({
                ...data.user,
                church_id: userInfo.church_id,
                church_name: userInfo.church_name,
                church_slug: userInfo.church_slug,
                role: userInfo.role,
                web_access: userInfo.web_access,
                comms_user_id: data.user.id  // V2: Always use Supabase UUID
              });
            } else {
              onAuth(data.user);
            }
          } catch (err) {
            console.error('Error fetching user info:', err);
            onAuth(data.user);
          }
        }
      }
    } catch (err) {
      console.error('Auth error:', err);
      setError(err.message || 'Unable to connect. Please check your internet connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      if (!email) {
        throw new Error('Please enter your email address');
      }

      const { success, error: resetError } = await resetPassword(email);

      if (resetError) {
        throw resetError;
      }

      if (success) {
        if (onRecoveryErrorClear) onRecoveryErrorClear();
        setSuccessMessage('Password reset link sent! Please check your email inbox.');
      } else {
        throw new Error('Failed to send reset email');
      }
    } catch (err) {
      console.error('Reset password error:', err);
      setError(err.message || 'Failed to send reset email. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  // Download App View (shown to team members after signup)
  if (showDownloadApp) {
    if (isStudio) {
      return (
        <StudioShell>
          <div className="st-au-card st-au-c">
            <DownloadAppContent email={email} variant="page" />
          </div>
        </StudioShell>
      );
    }
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 dark:from-[#1e1f22] to-slate-100 dark:to-[#2d2f33] flex items-center justify-center p-4">
        <DownloadAppContent email={email} variant="page" />
      </div>
    );
  }

  // Forgot Password View
  if (isForgotPassword) {
    const backToSignIn = (
      <button
        type="button"
        onClick={() => {
          setIsForgotPassword(false);
          setError('');
          setSuccessMessage('');
        }}
        className={isStudio ? 'st-au-lnk' : 'text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-200 font-medium transition-colors'}
      >
        {isStudio ? 'Back to sign in' : '← Back to Sign In'}
      </button>
    );
    const Shell = isStudio ? StudioShell : ClassicShell;
    return (
      <Shell>
        <div data-ui="auth-card" className={isStudio ? 'st-au-card' : 'bg-card rounded-2xl shadow-xl shadow-slate-200/50 p-8 border border-slate-100 dark:border-white/10'}>
          {isStudio ? (
            <div className="st-au-head">
              <h2 className="st-au-t">Reset password</h2>
              <p className="st-au-s">Enter your email and we&apos;ll send you a reset link</p>
            </div>
          ) : (
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-2">Reset Password</h2>
              <p className="text-slate-500 dark:text-slate-400">Enter your email and we&apos;ll send you a reset link</p>
            </div>
          )}

          <form onSubmit={handleForgotPassword} className={isStudio ? 'st-au-form' : 'space-y-5'}>
            {recoveryError && (isStudio ? (
              <div className="st-au-notice st-au-warn" data-testid="recovery-error-banner">
                <AlertCircle className="w-4 h-4" />
                <span>{recoveryError}</span>
              </div>
            ) : (
              <div className="p-4 text-sm text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/40 rounded-xl border border-amber-200 dark:border-amber-900/50 flex items-start gap-2" data-testid="recovery-error-banner">
                <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <span>{recoveryError}</span>
              </div>
            ))}
            {error && (isStudio ? <StudioNotice kind="bad">{error}</StudioNotice> : (
              <div className="p-4 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/40 rounded-xl border border-red-100 flex items-center gap-2">
                <div className="w-5 h-5 rounded-full bg-red-100 dark:bg-red-900/50 flex items-center justify-center flex-shrink-0">
                  <span className="text-red-600 dark:text-red-400 text-xs">!</span>
                </div>
                {error}
              </div>
            ))}
            {successMessage && (isStudio ? <StudioNotice kind="ok">{successMessage}</StudioNotice> : (
              <div className="p-4 text-sm text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-950/40 rounded-xl border border-green-100 flex items-center gap-2">
                <Sparkles className="w-4 h-4 flex-shrink-0" />
                {successMessage}
              </div>
            ))}
            <div className={isStudio ? 'st-au-field' : 'space-y-2'}>
              <Label htmlFor="reset-email" className={isStudio ? undefined : 'text-slate-700 dark:text-slate-200 font-medium'}>Email address</Label>
              <Input
                id="reset-email"
                type="email"
                placeholder="you@church.org"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className={isStudio ? 'st-au-in' : 'h-12 bg-slate-50 dark:bg-[#1e1f22] border-slate-200 dark:border-white/10 focus:bg-white dark:focus:bg-[#2d2f33] focus:border-indigo-500 focus:ring-indigo-500/20 transition-all placeholder:text-slate-400 dark:placeholder:text-slate-500'}
              />
            </div>
            <Button
              type="submit"
              className={isStudio
                ? `st-au-btn st-au-pri st-au-lg st-au-full${loading ? ' st-au-busy' : ''}`
                : 'w-full h-12 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-xl shadow-lg shadow-indigo-500/25 hover:shadow-xl hover:shadow-indigo-500/30 transition-all'}
              disabled={loading}
            >
              {loading ? (isStudio ? (
                <><span className="st-au-spin" /><span>Sending…</span></>
              ) : (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  Sending...
                </span>
              )) : (isStudio ? 'Send reset link' : 'Send Reset Link')}
            </Button>
          </form>

          {isStudio ? <div className="st-au-foot">{backToSignIn}</div> : <div className="mt-6 text-center">{backToSignIn}</div>}
        </div>
      </Shell>
    );
  }

  const footerLink = !hideSignupOption && (
    isSignUp ? (
      <button
        type="button"
        onClick={() => {
          setIsSignUp(false);
          setError('');
          setSuccessMessage('');
          handleRemovePicture();
        }}
        className={isStudio ? undefined : 'text-sm text-slate-600 dark:text-slate-300 hover:text-slate-800 dark:hover:text-white transition-colors'}
      >
        Already have an account? <span className={isStudio ? 'st-au-lnk' : 'text-indigo-600 dark:text-indigo-400 font-medium'}>Sign in</span>
      </button>
    ) : (
      <a
        href="/signup"
        className={isStudio ? undefined : 'text-sm text-slate-600 dark:text-slate-300 hover:text-slate-800 dark:hover:text-white transition-colors'}
        data-testid="signup-link"
      >
        Don&apos;t have an account? <span className={isStudio ? 'st-au-lnk' : 'text-indigo-600 dark:text-indigo-400 font-medium'}>Sign up</span>
      </a>
    )
  );

  // ---- Studio (doc 119 §24): the same controls in the 440-card language ----
  if (isStudio) {
    const submitBusy = loading || uploadingPicture;
    return (
      <StudioShell>
        <div data-ui="auth-card" className="st-au-card">
          {loadingInvite ? (
            /* the invite fetch is a skeleton in the form's shape (mock frame 8) */
            <div className="st-au-skel">
              <span className="st-au-sk" style={{ width: '190px', height: '22px' }} />
              <span className="st-au-sk" style={{ width: '260px', height: '14px', marginTop: '-6px' }} />
              <div className="st-au-f" style={{ marginTop: '8px' }}>
                <span className="st-au-sk" style={{ width: '70px', height: '13px' }} />
                <span className="st-au-sk" style={{ width: '100%', height: '36px' }} />
              </div>
              <div className="st-au-f">
                <span className="st-au-sk" style={{ width: '90px', height: '13px' }} />
                <span className="st-au-sk" style={{ width: '100%', height: '36px' }} />
              </div>
              <div className="st-au-f">
                <span className="st-au-sk" style={{ width: '60px', height: '13px' }} />
                <span className="st-au-sk" style={{ width: '100%', height: '36px' }} />
              </div>
              <span className="st-au-sk" style={{ width: '100%', height: '40px', marginTop: '6px' }} />
            </div>
          ) : (inviteToken && !inviteInfo) ? (
            <div data-testid="invite-expired-screen">
              <div className="st-au-head st-au-c">
                <span className="st-au-glyph st-au-bad"><AlertCircle /></span>
                <h2 className="st-au-t">Invitation expired</h2>
                <p className="st-au-s">This link is no longer valid. Please ask your leader for a new invitation.</p>
              </div>
              <div className="st-au-foot">
                <a href="/" className="st-au-lnk" data-testid="back-to-signin-link">Back to sign in</a>
              </div>
            </div>
          ) : (
            <>
              <div className="st-au-head">
                {inviteInfo ? (
                  <>
                    <h2 className="st-au-t">You&apos;re invited! 🎉</h2>
                    <p className="st-au-s">
                      Join <b>{inviteInfo.church_name}</b> as {inviteInfo.role === 'admin' ? 'an admin' : 'a team member'}
                    </p>
                  </>
                ) : (
                  <>
                    <h2 className="st-au-t">{isSignUp ? 'Create your account' : 'Welcome back'}</h2>
                    <p className="st-au-s">{isSignUp ? 'Start leading your team today' : 'Sign in to continue to your dashboard'}</p>
                  </>
                )}
              </div>

              <form onSubmit={handleAuth} className="st-au-form">
                {error && <StudioNotice kind="bad">{error}</StudioNotice>}
                {successMessage && <StudioNotice kind="ok">{successMessage}</StudioNotice>}

                {isSignUp && (
                  <>
                    <div className="st-au-field">
                      <label>Profile picture (optional)</label>
                      <div className="st-au-avrow">
                        <div className="st-au-avwrap" onClick={() => fileInputRef.current?.click()}>
                          {profilePicturePreview ? (
                            <div className="st-au-photo">
                              <LocalImageThumb
                                src={profilePicturePreview}
                                name={profilePicture?.name}
                                alt="Profile preview"
                                className="w-full h-full object-cover"
                              />
                            </div>
                          ) : (
                            <div className="st-au-av"><span>{name ? getInitials(name) : '?'}</span></div>
                          )}
                          <span className="st-au-scrim"><Camera className="w-[18px] h-[18px]" /></span>
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept={IMAGE_ACCEPT}
                            onChange={handlePictureSelect}
                            className="hidden"
                            data-testid="signup-profile-picture-input"
                          />
                        </div>
                        <div>
                          {profilePicturePreview ? (
                            <div className="flex items-center gap-1.5">
                              <span className="st-au-okline">Photo selected</span>
                              <button type="button" onClick={handleRemovePicture} className="st-au-xbtn" aria-label="Remove photo">
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          ) : (
                            <>
                              <div className="st-au-n">Click to upload</div>
                              <div className="st-au-fhelp">Max 5 MB</div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="st-au-field">
                      <Label htmlFor="name">Your name</Label>
                      <Input id="name" type="text" placeholder="John Smith" value={name}
                             onChange={(e) => setName(e.target.value)} required className="st-au-in" />
                    </div>
                    {!inviteToken && (
                      <div className="st-au-field">
                        <Label htmlFor="churchName">Church name</Label>
                        <div className="st-au-fwrap">
                          <Building2 className="st-au-leadicon" />
                          <Input id="churchName" type="text" placeholder="Grace Community Church" value={churchName}
                                 onChange={(e) => setChurchName(e.target.value)} required className="st-au-in st-au-lead" />
                        </div>
                      </div>
                    )}

                    <div className="st-au-field">
                      <Label>Birthday</Label>
                      <div className="st-au-bday">
                        <select
                          data-testid="signup-birthday-month"
                          value={birthdayMonth}
                          onChange={(e) => setBirthdayMonth(e.target.value)}
                          className={birthdayMonth ? undefined : 'st-au-ph'}
                        >
                          <option value="">Month</option>
                          {['January','February','March','April','May','June','July','August','September','October','November','December'].map((m, i) => (
                            <option key={i} value={String(i + 1)}>{m}</option>
                          ))}
                        </select>
                        <select
                          data-testid="signup-birthday-day"
                          value={birthdayDay}
                          onChange={(e) => setBirthdayDay(e.target.value)}
                          className={birthdayDay ? undefined : 'st-au-ph'}
                        >
                          <option value="">Day</option>
                          {Array.from({length: 31}, (_, i) => (
                            <option key={i} value={String(i + 1)}>{i + 1}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </>
                )}

                <div className="st-au-field">
                  <Label htmlFor="email">Email address</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@church.org"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    readOnly={!!inviteInfo}
                    className={inviteInfo ? 'st-au-in st-au-ro' : 'st-au-in'}
                  />
                  {inviteInfo && <p className="st-au-fhelp">Email address is set by your invitation</p>}
                </div>

                <div className="st-au-field">
                  <div className="st-au-lrow">
                    <Label htmlFor="password">Password</Label>
                    {!isSignUp && (
                      <button
                        type="button"
                        onClick={() => {
                          setIsForgotPassword(true);
                          setError('');
                          setSuccessMessage('');
                        }}
                        className="st-au-lnk st-au-s13"
                      >
                        Forgot password?
                      </button>
                    )}
                  </div>
                  <div className="st-au-fwrap">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="st-au-in st-au-trail"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(p => !p)}
                      className="st-au-eye"
                      tabIndex={-1}
                      data-testid="login-password-toggle"
                    >
                      {showPassword ? <EyeOff /> : <Eye />}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  className={`st-au-btn st-au-pri st-au-lg st-au-full${submitBusy ? ' st-au-busy' : ''}`}
                  disabled={submitBusy}
                >
                  {submitBusy ? (
                    uploadingPicture ? (
                      <>
                        <UploadProgress progress={avatarUploadProgress} size={15} className="text-current" />
                        <span>
                          {avatarUploadProgress >= 90 && avatarUploadProgress < 100
                            ? 'Processing photo…'
                            : `Uploading photo… ${avatarUploadProgress}%`}
                        </span>
                      </>
                    ) : (
                      <>
                        <span className="st-au-spin" />
                        <span>{isSignUp ? 'Creating account…' : 'Signing in…'}</span>
                      </>
                    )
                  ) : isSignUp ? 'Create account' : 'Sign in'}
                </Button>
              </form>

              {footerLink && <div className="st-au-foot">{footerLink}</div>}
            </>
          )}
        </div>
      </StudioShell>
    );
  }

  return (
    <ClassicShell>
      <div data-ui="auth-card" className="bg-card rounded-2xl shadow-xl shadow-slate-200/50 p-8 border border-slate-100 dark:border-white/10">
        {/* Show loading state while fetching invite info */}
        {loadingInvite ? (
          <div className="text-center py-8">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-500 mx-auto mb-4" />
            <p className="text-slate-500 dark:text-slate-400">Loading invitation...</p>
          </div>
        ) : (inviteToken && !inviteInfo) ? (
          <div className="text-center py-8" data-testid="invite-expired-screen">
            <div className="w-14 h-14 rounded-full bg-red-50 dark:bg-red-950/40 flex items-center justify-center mx-auto mb-5">
              <AlertCircle className="w-7 h-7 text-red-500 dark:text-red-400" />
            </div>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-3">
              Invitation Expired
            </h2>
            <p className="text-slate-500 dark:text-slate-400 mb-6 max-w-sm mx-auto">
              This link is no longer valid. Please ask your leader for a new invitation.
            </p>
            <a
              href="/"
              className="inline-flex items-center text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-200 font-medium transition-colors"
              data-testid="back-to-signin-link"
            >
              Back to sign in
            </a>
          </div>
        ) : (
          <>
            <div className="text-center mb-8">
              {inviteInfo ? (
                <>
                  <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-2">
                    You&apos;re Invited! 🎉
                  </h2>
                  <p className="text-slate-500 dark:text-slate-400">
                    Join <span className="font-semibold text-indigo-600 dark:text-indigo-400">{inviteInfo.church_name}</span> as {inviteInfo.role === 'admin' ? 'an Admin' : 'a Team Member'}
                  </p>
                </>
              ) : (
                <>
                  <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-2">
                    {isSignUp ? 'Create your account' : 'Welcome back'}
                  </h2>
                  <p className="text-slate-500 dark:text-slate-400">
                    {isSignUp ? 'Start leading your team today' : 'Sign in to continue to your dashboard'}
                  </p>
                </>
              )}
            </div>

            <form onSubmit={handleAuth} className="space-y-5">
              {error && (
                <div className="p-4 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/40 rounded-xl border border-red-100 flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full bg-red-100 dark:bg-red-900/50 flex items-center justify-center flex-shrink-0">
                    <span className="text-red-600 dark:text-red-400 text-xs font-bold">!</span>
                  </div>
                  {error}
                </div>
              )}
              {successMessage && (
                <div className="p-4 text-sm text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-950/40 rounded-xl border border-green-100 flex items-center gap-2">
                  <Sparkles className="w-4 h-4 flex-shrink-0" />
                  {successMessage}
                </div>
              )}

              {isSignUp && (
                <>
                  {/* Profile Picture (Optional) */}
                  <div className="space-y-2">
                    <Label className="text-slate-700 dark:text-slate-200 font-medium flex items-center gap-2">
                      <Camera className="w-4 h-4 text-slate-400 dark:text-slate-500" />
                      Profile Picture <span className="text-slate-400 dark:text-slate-500 text-xs">(optional)</span>
                    </Label>
                    <div className="flex items-center gap-4">
                      <div 
                        className="relative cursor-pointer group"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        {profilePicturePreview ? (
                          <div className="w-16 h-16 rounded-xl overflow-hidden border-2 border-indigo-500 shadow-lg">
                            <LocalImageThumb
                              src={profilePicturePreview}
                              name={profilePicture?.name}
                              alt="Profile preview"
                              className="w-full h-full object-cover"
                            />
                          </div>
                        ) : (
                          <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center border-2 border-slate-200 dark:border-white/10">
                            <span className="text-xl font-bold text-white">
                              {name ? getInitials(name) : '?'}
                            </span>
                          </div>
                        )}
                        
                        <div className="absolute inset-0 rounded-xl bg-black/50 opacity-0 group-hover:opacity-100 transition-all duration-200 flex items-center justify-center">
                          <Camera className="w-5 h-5 text-white" />
                        </div>
                        
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept={IMAGE_ACCEPT}
                          onChange={handlePictureSelect}
                          className="hidden"
                          data-testid="signup-profile-picture-input"
                        />
                      </div>
                      
                      <div className="flex-1">
                        {profilePicturePreview ? (
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-green-600 dark:text-green-400">Photo selected</span>
                            <button
                              type="button"
                              onClick={handleRemovePicture}
                              className="text-slate-400 dark:text-slate-500 hover:text-red-500 transition-colors"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <div>
                            <p className="text-sm text-slate-500 dark:text-slate-400">Click to upload</p>
                            <p className="text-xs text-slate-400 dark:text-slate-500">Max 5MB</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-slate-700 dark:text-slate-200 font-medium">Your name</Label>
                    <Input
                      id="name"
                      type="text"
                      placeholder="John Smith"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                      className="h-12 bg-slate-50 dark:bg-[#1e1f22] border-slate-200 dark:border-white/10 focus:bg-white dark:focus:bg-[#2d2f33] focus:border-indigo-500 focus:ring-indigo-500/20 transition-all placeholder:text-slate-400 dark:placeholder:text-slate-500"
                    />
                  </div>
                  {!inviteToken && (
                    <div className="space-y-2">
                      <Label htmlFor="churchName" className="text-slate-700 dark:text-slate-200 font-medium">Church name</Label>
                      <div className="relative">
                        <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 dark:text-slate-500" />
                        <Input
                          id="churchName"
                          type="text"
                          placeholder="Grace Community Church"
                          value={churchName}
                          onChange={(e) => setChurchName(e.target.value)}
                          required
                          className="h-12 pl-11 bg-slate-50 dark:bg-[#1e1f22] border-slate-200 dark:border-white/10 focus:bg-white dark:focus:bg-[#2d2f33] focus:border-indigo-500 focus:ring-indigo-500/20 transition-all placeholder:text-slate-400 dark:placeholder:text-slate-500"
                        />
                      </div>
                    </div>
                  )}

                  {/* Birthday (Required) */}
                  <div className="space-y-2">
                    <Label className="text-slate-700 dark:text-slate-200 font-medium flex items-center gap-2">
                      <Cake className="w-4 h-4 text-slate-400 dark:text-slate-500" />
                      Birthday <span className="text-red-500 dark:text-red-400 text-xs">*required</span>
                    </Label>
                    <div className="flex gap-3">
                      <select
                        data-testid="signup-birthday-month"
                        value={birthdayMonth}
                        onChange={(e) => setBirthdayMonth(e.target.value)}
                        className="flex-1 h-12 px-3 rounded-md border border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-[#1e1f22] text-sm focus:bg-white dark:focus:bg-[#2d2f33] focus:border-indigo-500 focus:outline-none"
                      >
                        <option value="">Month</option>
                        {['January','February','March','April','May','June','July','August','September','October','November','December'].map((m, i) => (
                          <option key={i} value={String(i + 1)}>{m}</option>
                        ))}
                      </select>
                      <select
                        data-testid="signup-birthday-day"
                        value={birthdayDay}
                        onChange={(e) => setBirthdayDay(e.target.value)}
                        className="w-24 h-12 px-3 rounded-md border border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-[#1e1f22] text-sm focus:bg-white dark:focus:bg-[#2d2f33] focus:border-indigo-500 focus:outline-none"
                      >
                        <option value="">Day</option>
                        {Array.from({length: 31}, (_, i) => (
                          <option key={i} value={String(i + 1)}>{i + 1}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </>
              )}

              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-700 dark:text-slate-200 font-medium">Email address</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@church.org"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  readOnly={!!inviteInfo}
                  className={`h-12 border-slate-200 dark:border-white/10 focus:border-indigo-500 focus:ring-indigo-500/20 transition-all placeholder:text-slate-400 dark:placeholder:text-slate-500 ${
                    inviteInfo ? 'bg-slate-100 dark:bg-[#3a3d41] cursor-not-allowed' : 'bg-slate-50 dark:bg-[#1e1f22] focus:bg-white dark:focus:bg-[#2d2f33]'
                  }`}
                />
                {inviteInfo && (
                  <p className="text-xs text-slate-500 dark:text-slate-400">Email address is set by your invitation</p>
                )}
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-slate-700 dark:text-slate-200 font-medium">Password</Label>
                  {!isSignUp && (
                    <button
                      type="button"
                      onClick={() => {
                        setIsForgotPassword(true);
                        setError('');
                        setSuccessMessage('');
                      }}
                      className="text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-200 font-medium transition-colors"
                    >
                      Forgot password?
                    </button>
                  )}
                </div>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="h-12 pr-11 bg-slate-50 dark:bg-[#1e1f22] border-slate-200 dark:border-white/10 focus:bg-white dark:focus:bg-[#2d2f33] focus:border-indigo-500 focus:ring-indigo-500/20 transition-all placeholder:text-slate-400 dark:placeholder:text-slate-500"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(p => !p)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
                    tabIndex={-1}
                    data-testid="login-password-toggle"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full h-12 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-xl shadow-lg shadow-indigo-500/25 hover:shadow-xl hover:shadow-indigo-500/30 transition-all"
                disabled={loading || uploadingPicture}
              >
                {loading || uploadingPicture ? (
                  <span className="flex items-center gap-2">
                    {uploadingPicture ? (
                      <>
                        <UploadProgress progress={avatarUploadProgress} size={18} className="text-white" />
                        <span>
                          {avatarUploadProgress >= 90 && avatarUploadProgress < 100
                            ? 'Processing photo…'
                            : `Uploading photo… ${avatarUploadProgress}%`}
                        </span>
                      </>
                    ) : (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>Please wait...</span>
                      </>
                    )}
                  </span>
                ) : isSignUp ? 'Create Account' : 'Sign In'}
              </Button>
            </form>

            {footerLink && <div className="mt-6 text-center">{footerLink}</div>}
          </>
        )}
      </div>
    </ClassicShell>
  );
}

export default AuthPage;
